
import { getEffectiveDate, isConsecutiveDay, formatDate, isSameDay } from "@/lib/dateUtils";
import { isDebugDateMode } from "@/lib/debugUtils";

export class StreakCalculator {
  calculateNewStreak(
    isWin: boolean,
    currentStreak: number,
    maxStreak: number,
    lastWinDate: string | null,
    gameMode: 'daily' | 'practice' | 'custom' = 'daily'
  ): { currentStreak: number; maxStreak: number } {
    const isDebugMode = isDebugDateMode();
    const effectiveDate = getEffectiveDate();
    
    console.log('Starting streak calculation:', {
      isWin,
      currentStreak,
      maxStreak,
      lastWinDate,
      effectiveDate: effectiveDate.toISOString(),
      isDebugMode,
      gameMode,
      timestamp: new Date().toISOString()
    });

    // Always reset streak on loss
    if (!isWin) {
      console.log('Loss detected, resetting streak to 0');
      return { 
        currentStreak: 0, 
        maxStreak // Preserve max streak on loss
      };
    }
    
    // For first win ever (no last win date)
    if (!lastWinDate) {
      console.log('First win ever detected, setting streak to 1');
      return {
        currentStreak: 1,
        maxStreak: Math.max(1, maxStreak)
      };
    }

    // Parse stored YYYY-MM-DD as a LOCAL date to avoid UTC offset issues
    const parseLocalDate = (s: string) => {
      try {
        const [y, m, d] = s.split('-').map(Number);
        return new Date((y as number), (m as number) - 1, (d as number), 0, 0, 0, 0);
      } catch {
        return new Date(s);
      }
    };
    const lastWin = parseLocalDate(lastWinDate);
    
    // Different logic for practice vs daily mode
    if (gameMode === 'practice') {
      // In practice mode, always increment streak for any win
      const newStreak = currentStreak + 1;
      const newMaxStreak = Math.max(newStreak, maxStreak);

      console.log('Practice mode win detected, incrementing streak:', {
        newStreak,
        newMaxStreak,
        gameMode,
        lastWinDate,
        currentDate: effectiveDate.toISOString(),
        timestamp: new Date().toISOString()
      });

      return { 
        currentStreak: newStreak, 
        maxStreak: newMaxStreak 
      };
    } else {
      // Daily mode: Check if this is a consecutive day win
      const isConsecutive = isConsecutiveDay(lastWin, effectiveDate);
      const isSameDayWin = isSameDay(lastWin, effectiveDate);
      
      console.log('Checking daily mode win timing:', {
        lastWin: lastWin.toISOString(),
        effectiveDate: effectiveDate.toISOString(),
        formattedLastWin: formatDate(lastWin),
        formattedEffectiveDate: formatDate(effectiveDate),
        isConsecutive,
        isSameDayWin,
        isDebugMode,
        gameMode,
        currentStreak,
        timestamp: new Date().toISOString()
      });
      
      // In daily mode, don't allow multiple wins on the same day
      if (isSameDayWin) {
        console.log('Same day win in daily mode, keeping current streak:', {
          currentStreak,
          maxStreak,
          lastWinDate,
          effectiveDate: formatDate(effectiveDate),
          reason: 'Same day win detected'
        });
        return {
          currentStreak,
          maxStreak
        };
      }
      
      // If it's a consecutive day, increment streak
      if (isConsecutive) {
        const newStreak = currentStreak + 1;
        const newMaxStreak = Math.max(newStreak, maxStreak);

        console.log('Consecutive day win detected in daily mode:', {
          newStreak,
          newMaxStreak,
          isConsecutive: true,
          lastWinDate,
          currentDate: effectiveDate.toISOString(),
          timestamp: new Date().toISOString()
        });

        return { 
          currentStreak: newStreak, 
          maxStreak: newMaxStreak 
        };
      }

      // If not consecutive day, start new streak at 1 (since it's still a win)
      console.log('Non-consecutive day detected in daily mode, starting new streak at 1');
      return {
        currentStreak: 1,
        maxStreak: Math.max(1, maxStreak)
      };
    }
  }

  getCurrentDate(): string {
    // Use getEffectiveDate to be debug-mode aware
    return formatDate(getEffectiveDate());
  }
}
